import React, { useEffect, useMemo, useState } from 'react';
import { CircleHelp, Pencil, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';

const ROLES = ['super_admin', 'admin', 'manager', 'staff', 'guest'];
const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];

const emptySection = () => ({
  id: `section-${Date.now()}`,
  heading: '',
  body: '',
  roles: [],
  actions: ['view'],
});

const ContextualHelp = ({ currentModule }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const canEditHelp = hasPermission('permissions-management', 'edit');
  const apiBase = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    setIsOpen(false);
    setIsEditing(false);
    setQuery('');
  }, [currentModule]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const controller = new AbortController();
    const loadArticle = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = {};
        if (window.__sessionId) headers['X-Session-ID'] = window.__sessionId;
        const response = await fetch(`${apiBase}/help/${encodeURIComponent(currentModule)}`, {
          credentials: 'include',
          headers,
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Failed to load help');
        setArticle(result.data);
        setDraft(result.data);
      } catch (loadError) {
        if (loadError.name !== 'AbortError') {
          setArticle(null);
          setDraft({ title: 'Screen Help', summary: '', sections: [] });
          setError(loadError.message);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadArticle();
    return () => controller.abort();
  }, [apiBase, currentModule, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const visibleSections = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return (article?.sections || []).filter(section => {
      const roles = Array.isArray(section.roles) ? section.roles : [];
      const actions = Array.isArray(section.actions) ? section.actions : [];
      const roleMatches = roles.length === 0 || roles.includes(user?.role);
      const permissionsMatch = actions.length === 0 || actions.every(action => hasPermission(currentModule, action));
      const textMatches = !normalizedQuery || `${section.heading} ${section.body}`.toLowerCase().includes(normalizedQuery);
      return roleMatches && permissionsMatch && textMatches;
    });
  }, [article, currentModule, hasPermission, query, user?.role]);

  const updateSection = (index, field, value) => {
    setDraft(current => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) => (
        sectionIndex === index ? { ...section, [field]: value } : section
      )),
    }));
  };

  const toggleSectionValue = (index, field, value) => {
    const values = draft.sections[index][field] || [];
    updateSection(index, field, values.includes(value)
      ? values.filter(item => item !== value)
      : [...values, value]);
  };

  const saveArticle = async () => {
    setSaving(true);
    setError('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (window.__sessionId) headers['X-Session-ID'] = window.__sessionId;
      const response = await fetch(`${apiBase}/help/${encodeURIComponent(currentModule)}`, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          title: draft.title,
          summary: draft.summary,
          sections: draft.sections,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to save help');
      setArticle(result.data);
      setDraft(result.data);
      setIsEditing(false);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-10 right-4 z-40 w-10 h-10 inline-flex items-center justify-center rounded-full bg-slate-800 text-white shadow-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 print:hidden"
        title="Help for this screen"
        aria-label="Open help for this screen"
      >
        <CircleHelp size={20} />
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[70] print:hidden" role="dialog" aria-modal="true" aria-label="Screen help">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setIsOpen(false)}
            aria-label="Close help"
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-slate-500">Screen help</p>
                <h2 className="mt-1 text-lg font-bold text-slate-900">
                  {isEditing ? 'Edit help content' : (article?.title || 'Help')}
                </h2>
                {!isEditing && article?.summary && <p className="mt-1 text-sm text-slate-600">{article.summary}</p>}
              </div>
              <div className="flex items-center gap-1">
                {canEditHelp && !isEditing && (
                  <button
                    type="button"
                    onClick={() => { setDraft(article || { title: 'Screen Help', summary: '', sections: [] }); setIsEditing(true); }}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                    title="Edit help content"
                    aria-label="Edit help content"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                  title="Close help"
                  aria-label="Close help"
                >
                  <X size={18} />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading && <p className="text-sm text-slate-500">Loading help...</p>}
              {error && <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

              {!loading && !isEditing && article && (
                <>
                  <label className="relative block mb-4">
                    <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                      type="search"
                      value={query}
                      onChange={event => setQuery(event.target.value)}
                      placeholder="Search this help"
                      className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-slate-500 focus:outline-none"
                    />
                  </label>

                  <div className="space-y-4">
                    {visibleSections.map(section => (
                      <section key={section.id || section.heading} className="border-l-2 border-amber-400 pl-3">
                        <h3 className="text-sm font-bold text-slate-800">{section.heading}</h3>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-600">{section.body}</p>
                      </section>
                    ))}
                    {visibleSections.length === 0 && (
                      <p className="text-sm text-slate-500">No help sections match your role, permissions, and search.</p>
                    )}
                  </div>
                </>
              )}

              {!loading && isEditing && draft && (
                <div className="space-y-4">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Title</span>
                    <input
                      value={draft.title}
                      onChange={event => setDraft(current => ({ ...current, title: event.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Summary</span>
                    <textarea
                      value={draft.summary}
                      onChange={event => setDraft(current => ({ ...current, summary: event.target.value }))}
                      rows={3}
                      className="mt-1 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                    />
                  </label>

                  {draft.sections.map((section, index) => (
                    <div key={section.id || index} className="rounded-md border border-slate-200 p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase text-slate-500">Section {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => setDraft(current => ({ ...current, sections: current.sections.filter((_, itemIndex) => itemIndex !== index) }))}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                          title="Remove section"
                          aria-label={`Remove section ${index + 1}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <input
                        value={section.heading}
                        onChange={event => updateSection(index, 'heading', event.target.value)}
                        placeholder="Section heading"
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                      />
                      <textarea
                        value={section.body}
                        onChange={event => updateSection(index, 'body', event.target.value)}
                        placeholder="Guidance"
                        rows={4}
                        className="w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
                      />
                      <fieldset>
                        <legend className="text-xs font-semibold text-slate-600">Roles (none means all)</legend>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {ROLES.map(role => (
                            <label key={role} className="inline-flex items-center gap-1 text-xs text-slate-600">
                              <input type="checkbox" checked={(section.roles || []).includes(role)} onChange={() => toggleSectionValue(index, 'roles', role)} />
                              {role.replace('_', ' ')}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <fieldset>
                        <legend className="text-xs font-semibold text-slate-600">Required permissions (none means all)</legend>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {ACTIONS.map(action => (
                            <label key={action} className="inline-flex items-center gap-1 text-xs text-slate-600">
                              <input type="checkbox" checked={(section.actions || []).includes(action)} onChange={() => toggleSectionValue(index, 'actions', action)} />
                              {action}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => setDraft(current => ({ ...current, sections: [...current.sections, emptySection()] }))}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Plus size={15} /> Add section
                  </button>
                </div>
              )}
            </div>

            {isEditing && (
              <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                <button
                  type="button"
                  onClick={() => { setDraft(article); setIsEditing(false); setError(''); }}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveArticle}
                  disabled={saving}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-800 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  <Save size={15} /> {saving ? 'Saving...' : 'Save help'}
                </button>
              </footer>
            )}
          </aside>
        </div>
      )}
    </>
  );
};

export default ContextualHelp;